import { useEffect, useState, useCallback } from "react";

declare const __BUILD_VERSION__: string;

const POLL_INTERVAL = 60_000; // check every 60 seconds
const NEW_VERSION_EVENT = "app:new-version-available";

/** Show the new-version banner, e.g. when Zero reports the client is outdated. */
export function notifyNewVersionAvailable() {
  window.dispatchEvent(new Event(NEW_VERSION_EVENT));
}

export function useNewVersionCheck() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  const checkVersion = useCallback(async () => {
    // Only check in production builds where __BUILD_VERSION__ is defined
    if (typeof __BUILD_VERSION__ === "undefined") return;

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.version && data.version !== __BUILD_VERSION__) {
        setNewVersionAvailable(true);
      }
    } catch {
      // Network error or offline — ignore
    }
  }, []);

  useEffect(() => {
    const onNewVersion = () => setNewVersionAvailable(true);
    window.addEventListener(NEW_VERSION_EVENT, onNewVersion);
    return () => window.removeEventListener(NEW_VERSION_EVENT, onNewVersion);
  }, []);

  useEffect(() => {
    if (typeof __BUILD_VERSION__ === "undefined") return;

    const interval = setInterval(checkVersion, POLL_INTERVAL);
    // Also check when the tab becomes visible again
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [checkVersion]);

  return newVersionAvailable;
}
